import React from 'react';
import t from 'shared/utils/translation';
import moment from 'moment-timezone';
import styled from 'styled-components';
import { SectionHeader } from '@grnhse/seedling/lib/azalea/typography';
import { fontWeight, colors } from '@grnhse/seedling/lib/azalea/constants';
import type { TimeZoneOption, Slot } from '../../types';
import { PrimaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { PrimaryButton as BonsaiPrimaryButton } from '@grnhse/bonsai/lib/components/buttons';
import { Body } from '@grnhse/bonsai/lib/components/typography';
import { useBonsai } from '@grnhse/bonsai/lib/hooks';

type Props = {
  selectedTime: Slot | null | undefined;
  interviewLength: number;
  selectedTimeZoneOption: TimeZoneOption;
  submit: () => void;
  submitting: boolean;
  isMobile?: boolean;
  next?: boolean;
  isReschedule?: boolean;
  bonsaiEnabled: boolean;
};

export default function ConfirmationBar(props: Props) {
  const {
    selectedTime,
    interviewLength,
    selectedTimeZoneOption,
    submit,
    isMobile,
    submitting,
    next,
    isReschedule,
    bonsaiEnabled,
  } = props;

  const bonsaiConfiguration = useBonsai();

  const selectedTimeZone = selectedTimeZoneOption.ianaTimeZoneId;

  const SelectedTime = isMobile
    ? bonsaiEnabled
      ? Body
      : SectionHeader
    : 'span';

  const fontFamily = bonsaiEnabled
    ? bonsaiConfiguration.secondaryFont
    : 'inherit';

  const submitText = () => {
    if (next) {
      return t('self_schedule.candidate_view.next');
    }

    if (isReschedule) {
      return t('self_schedule.candidate_view.reschedule_interview');
    }

    return t('self_schedule.submit');
  };

  return (
    // @ts-expect-error - TS2769 - No overload matches this call.
    <Wrapper isMobile={isMobile}>
      {/* @ts-expect-error - TS2769 - No overload matches this call. */}
      <Summary isMobile={isMobile}>
        {selectedTime && selectedTimeZoneOption && (
          <SelectedContainer>
            <SelectedLabel fontFamily={fontFamily}>
              {t('self_schedule.candidate_view.selected_time')}
            </SelectedLabel>{' '}
            <MobileBr />
            <span style={{ fontFamily: fontFamily }}>
              <SelectedTime data-provides="selected-time">
                {moment(selectedTime.start)
                  .tz(selectedTimeZone)
                  .format('dddd, MMMM Do YYYY, h:mma')}
                {t('self_schedule.dash')}
                {moment(selectedTime.start)
                  .add(interviewLength, 'minutes')
                  .tz(selectedTimeZone)
                  .format('h:mma')}{' '}
                {selectedTimeZoneOption.label}
              </SelectedTime>
            </span>
          </SelectedContainer>
        )}
        {bonsaiEnabled ? (
          <BonsaiPrimaryButton
            data-provides="schedule-button"
            ariaLabel={submitText()}
            onClick={submit}
            disabled={!selectedTime || submitting}
          >
            {submitText()}
          </BonsaiPrimaryButton>
        ) : (
          <StyledButton
            // @ts-expect-error - TS2769 - No overload matches this call.
            isMobile={isMobile}
            data-provides="schedule-button"
            disabled={!selectedTime || submitting}
            onClick={submit}
          >
            {submitText()}
          </StyledButton>
        )}
      </Summary>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  height: 90px;
  width: 100%;
  ${/* @ts-expect-error */ ''}
  border-top: ${({ isMobile }) =>
    isMobile ? 'none' : `1px solid ${colors.lightGrey}`};
  background: ${colors.white};

  @media only screen and (max-width: 480px) {
    position: inherit;
    height: 120px;
  }
`;

const StyledButton = styled(PrimaryButton)`
  display: inline-block;

  ${/* @ts-expect-error */ ''}
  ${({ isMobile }) =>
    isMobile &&
    ` width: 100%;
      text-align: center;
      display: block;
  `}
`;

const Summary = styled.div`
  display: flex;
  justify-content: flex-end;
  max-width: 1200px;
  margin: 30px auto 0;
  align-items: baseline;
  gap: 16px;

  @media only screen and (max-width: 1024px) {
    ${/* @ts-expect-error */ ''}
    margin: ${({ isMobile }) => (isMobile ? '0 25px' : '20px 60px 0')};
    align-items: center;
  }

  @media only screen and (max-width: 480px) {
    margin-top: 16px;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 16px;
  }
`;

const MobileBr = styled.br`
  display: none;
  @media only screen and (max-width: 1024px) {
    display: block;
  }
`;

const SelectedContainer = styled.span`
  flex: 1;
`;

const SelectedLabel = styled.label<{ fontFamily: string }>`
  font-weight: ${fontWeight.heavy};
  font-family: ${(props) => props.fontFamily};

  @media only screen and (max-width: 480px) {
    margin-bottom: 8px;
  }
`;
